import {
  V2ListServicesForAccountApiArg,
  V2PhaseResource,
  V2ProductResource,
  V2ResourceResource,
  V2ServiceCategoryResource,
  V2SubserviceResource,
} from "@generated";
import { useListCatalogServices } from "@common/hooks/serviceCatalog";
import { Subservice, Product } from "../../types";
import { Phase } from "@utils/helpers";
import { getResourceName } from "../../common";

export interface Standard {
  id: string;
  type?: "services";
  serviceType?: "professional_services" | "managed_services";
  name: string;
  category?: string;
  phase?: Phase;
  hours?: string;
  description?: string;
  subservices?: Subservice[];
  products?: Product[];
  resource?: { id: string; name: string };
}

const useListStandards = ({
  pageSize,
  currentPage,
  filterParams,
  sortBy,
  sortOrder,
  resourceRates,
  resourceRatesEnabled,
  skip,
}: {
  pageSize: number;
  currentPage: number;
  filterParams: V2ListServicesForAccountApiArg["filter"];
  sortBy: string;
  sortOrder: string;
  resourceRates: any[];
  resourceRatesEnabled: boolean;
  skip?: boolean;
}) => {
  let customError: string | null = null; // Declare customError
  let pageMeta = {
    recordCount: 0,
    pageCount: 0,
  };

  const { data, error, isLoading, refetch, isFetching } =
    useListCatalogServices({
      include: [
        "subservices",
        "products",
        "service-category",
        "phase",
        "resource",
        "subservices.resource",
      ] as V2ListServicesForAccountApiArg["include"],
      page: { number: currentPage, size: pageSize },
      filter: { ...filterParams, state: ["active"] },
      sort: sortBy
        ? ([
            sortOrder == "asc" ? sortBy : `-${sortBy}`,
          ] as V2ListServicesForAccountApiArg["sort"])
        : undefined,
    }, { skip });

  let standards: Standard[] = [];

  if (data?.data) {
    try {
      //find a resource-rate that matches both the service-lob-id && parent resource-id to service resource
      standards = data.data.map((standard) => {
        if (!standard.id) {
          throw new Error("Missing standard.id");
        }
        if (!standard.attributes?.name) {
          throw new Error("Missing standard.attributes.name");
        }

        const serviceType =
          standard?.attributes?.["service-type"] || "professional_services";

        const phase = data?.included?.find(
          (i) =>
            i.type === "phases" &&
            i.id === standard?.relationships?.phase?.data?.id
        ) as V2PhaseResource;

        const category = data?.included?.find(
          (i) =>
            i.type === "service-categories" &&
            i.id === standard?.relationships?.["service-category"]?.data?.id
        ) as V2ServiceCategoryResource;
        const categoryLOB = category?.attributes?.["lob-id"];
        const serviceLOB = standard?.attributes?.["lob-id"];

        const resourceId = standard?.relationships?.resource?.data?.id;
        const filteredResourceRates =
          resourceRatesEnabled && resourceId
            ? resourceRates.filter(
                (rr) => rr?.relationships?.resource?.data?.id == resourceId
              )
            : [];

        const resourceRateForServiceCategory = categoryLOB
          ? filteredResourceRates.find(
              (rr) =>
                rr?.relationships?.["line-of-business"]?.data?.id == categoryLOB
            )
          : undefined;
        const resourceRateForServiceLOB = serviceLOB
          ? filteredResourceRates.find(
              (rr) =>
                rr?.relationships?.["line-of-business"]?.data?.id == serviceLOB
            )
          : undefined;

        const resource =
          // If there’s a resource rate for the resource aligned to the service category for the service, then use that rate;
          resourceRateForServiceCategory ||
          // Else, if there’s a resource rate for the resource aligned to the lob of the service, then use that rate;
          resourceRateForServiceLOB ||
          // Else, if there’s a resource rate for the resource aligned to NO lob/SC then use that rate.
          filteredResourceRates?.[0] ||
          // Else, if project-resource-rate setting is off, use standard resource, otherwise leave blank
          !resourceRatesEnabled
            ? data?.included?.find(
                (i) => i.type === "resources" && i.id == resourceId
              )
            : undefined;

        const subservices = (standard?.relationships?.subservices?.data || [])
          .map((sub) => {
            const s = data?.included?.find(
              (i) => i.type === "subservices" && i.id == sub.id
            ) as V2SubserviceResource;

            const subResourceId = s?.relationships?.resource?.data?.id;
            const subResource = subResourceId
              ? (data?.included?.find(
                  (i) => i.type === "resources" && i.id == subResourceId
                ) as V2ResourceResource)
              : undefined;
            return {
              id: String(s?.id),
              type: "subservices" as "subservices",
              active: s?.attributes?.active || false,
              serviceType,
              name: s?.attributes?.name || "",
              quantity: s?.attributes?.quantity
                ? String(s.attributes.quantity)
                : "",
              hours: s?.attributes?.["suggested-hours"]
                ? //@ts-ignore
                  parseFloat(s.attributes["suggested-hours"]).toFixed(1)
                : "",
              resource: subResource?.id
                ? {
                    id: `resources-${subResource.id}`,
                    name: subResource?.attributes?.name || "",
                  }
                : resource?.id
                ? {
                    id: `${resource?.type}-${resource.id}`,
                    name: getResourceName(resource),
                  }
                : undefined,
              serviceId: String(standard.id),
            };
          })
          .filter((sub) => sub?.active);

        const products = (standard?.relationships?.products?.data || []).map(
          (product) => {
            const p = data?.included?.find(
              (i) => i.type === "products" && i.id === product.id
            ) as V2ProductResource;
            return {
              id: Number(p?.id) || 0,
              name: p?.attributes?.name || "",
              mpn: p?.attributes?.["manufacturer-part-number"] || "",
              price: p?.attributes?.["unit-price"]
                ? //@ts-ignore
                  parseFloat(p.attributes["unit-price"]).toFixed(1)
                : "",
            };
          }
        );

        return {
          id: String(standard.id),
          type: "services",
          serviceType,
          name: standard.attributes.name,
          category:
            category?.attributes?.["nested-name"] || category?.attributes?.name,
          phase:
            serviceType === "professional_services"
              ? {
                  id: Number(phase?.id) || -1,
                  name: phase?.attributes?.name || "",
                  position: phase?.attributes?.position || 999999,
                }
              : { id: -1, name: "Managed Services", position: 999999 },
          hours: standard.attributes?.["total-hours"]
            ? //@ts-ignore
              parseFloat(standard.attributes["total-hours"]).toFixed(1)
            : "",
          unitHours: standard.attributes?.["suggested-hours"]
            ? //@ts-ignore
              parseFloat(standard.attributes["suggested-hours"]).toFixed(1)
            : "",
          quantity: String(standard?.attributes?.["minimum-quantity"] || ""),
          description: standard.attributes?.["service-description"] || "",
          subservices,
          products,
          resource: resource?.id
            ? {
                id: `${resource?.type}-${resource.id}`,
                name: getResourceName(resource),
              }
            : undefined,
        };
      });

      if (!data.meta) {
        throw new Error("Missing data.meta");
      }

      if (!data.meta["record-count"]) {
        throw new Error('Missing data.meta["record-count"]');
      }

      if (!data.meta["page-count"]) {
        throw new Error('Missing data.meta["page-count"]');
      }
      pageMeta = {
        recordCount: data.meta["record-count"],
        pageCount: data.meta["page-count"],
      };
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }
  return {
    standards,
    isLoading,
    error: error ?? customError,
    pageMeta,
    refetch,
    isFetching,
  };
};
export default useListStandards;
